import React, { useState, useRef } from 'react';
import { getProductImageMedium } from '../../data/productImages';

interface HoverPreviewProps {
  sku: string;
  category: string;
  /** Titolo/descrizione prodotto da mostrare sotto l'immagine ingrandita */
  title?: string;
  /** Marca da mostrare come riga secondaria */
  brand?: string;
  children: React.ReactNode;
}

export function HoverPreview({ sku, category, title, brand, children }: HoverPreviewProps) {
  const [show, setShow] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imgSrc = getProductImageMedium(sku, category);

  const handleEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPos({ x: rect.right + 16, y: rect.top });
    timeoutRef.current = setTimeout(() => setShow(true), 60);
  };

  const handleLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(false);
    setImgLoaded(false);
  };

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative group cursor-zoom-in"
    >
      {/* Micro-zoom in-place sul thumbnail al passaggio del mouse.
          Wrapper di transform: scale fluida senza scuotere le righe della
          tabella perché il thumbnail ha overflow visibile e dimensione
          costante (la cella resta della stessa altezza). */}
      <div className="transition-transform duration-200 ease-out group-hover:scale-[1.18] group-hover:drop-shadow-md will-change-transform origin-center">
        {children}
      </div>
      {show && (
        <div
          className="fixed z-[70] pointer-events-none animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: Math.min(pos.x, window.innerWidth - 380),
            top: Math.max(8, Math.min(pos.y - 60, window.innerHeight - 380))
          }}
        >
          <div className="w-[360px] rounded-xl border-2 border-[hsl(214,58%,41%)]/30 bg-white shadow-2xl overflow-hidden">
            <div className="relative w-full h-[280px] bg-gradient-to-br from-secondary/40 to-white">
              {!imgLoaded && (
                <div className="absolute inset-0 bg-secondary/40 animate-pulse" />
              )}
              <img
                src={imgSrc}
                alt={sku}
                className={`w-full h-full object-contain p-4 transition-opacity duration-150 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="eager"
                onLoad={() => setImgLoaded(true)}
              />
            </div>
            <div className="px-3.5 py-2.5 border-t bg-[hsl(var(--dtk-canvas))]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono font-bold text-[hsl(214,58%,41%)] truncate">{sku}</span>
                {brand && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-white px-1.5 py-0.5 rounded border">
                    {brand}
                  </span>
                )}
              </div>
              {title && (
                <div className="text-xs text-foreground/80 mt-1 line-clamp-2">{title}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
